import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import OverviewTab from "./results/OverviewTab";
import SubdomainsTab from "./results/SubdomainsTab";
import VulnerabilitiesTab from "./results/VulnerabilitiesTab";
import ServerTechTab from "./results/ServerTechTab";
import SingleSelectApiDropdown from "../../components/SingleSelectApiDropdown";
import {
  Loader2,
  ShieldAlert,
  Globe,
  Server,
  Info,
  ChevronLeft
} from "lucide-react";
import type { Finding, ScanResultsData } from "./results/types";
import { useClientLevel } from "../../hooks/useClientLevel";

export default function ExternalScanResults() {
  const level = useClientLevel();
  const clientId = level?.id;
  const navigate = useNavigate();
  const initialClientIdRef = useRef(clientId);

  useEffect(() => {
    if (!clientId || level?.name === "global") {
      navigate("/dashboard", { replace: true });
    } else if (clientId !== initialClientIdRef.current) {
      navigate("/external-scan", { replace: true });
    }
  }, [clientId, level, navigate]);

  const { assetId } = useParams<{ assetId: string }>();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("jobid") || searchParams.get("jobId") || "";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ScanResultsData | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "subdomains" | "findings" | "server">("overview");
  const [currentJob, setCurrentJob] = useState<any>(null);

  // Dynamic filter state passed to VulnerabilitiesTab
  const [vulnerabilitySeverityFilter, setVulnerabilitySeverityFilter] = useState("ALL");

  useEffect(() => {
    const fetchJobs = async () => {
      if (!assetId) return;
      try {
        const response = await baseapi.get(`/external_scan/jobs/${assetId}`);
        if (response.data && response.data.status && response.data.data) {
          const match = response.data.data.find((j: any) => j.job_id === jobId);
          if (match) {
            setCurrentJob(match);
          } else {
            setCurrentJob({ job_id: jobId });
          }
        }
      } catch {
        setCurrentJob({ job_id: jobId });
      }
    };
    fetchJobs();
  }, [assetId, jobId]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!jobId) return;
      setLoading(true);
      setData(null);
      setActiveTab("overview");
      setVulnerabilitySeverityFilter("ALL");

      try {
        const response = await baseapi.get(`/external_scan/results/${jobId}`);
        if (response.data && response.data.status && response.data.results) {
          setData(response.data.results);
        } else {
          showError("No scan results found for this job ID.");
        }
      } catch (error: any) {
        showError(error.message || "Failed to fetch scan results");
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [jobId]);

  // Flatten findings from all scan modules
  const getFlatFindings = (): Finding[] => {
    if (!data || !data.findings) return [];
    return Object.values(data.findings).flat();
  };

  const flatFindings = getFlatFindings();

  // Compute severity statistics
  const getSeverityStats = () => {
    const stats = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 };
    flatFindings.forEach((f) => {
      const sev = (f.severity || "INFO").toUpperCase() as keyof typeof stats;
      if (stats[sev] !== undefined) {
        stats[sev]++;
      } else {
        stats.INFO++;
      }
    });
    return stats;
  };

  const severityStats = getSeverityStats();

  // Helper to extract target domain name
  const getTargetDomain = () => {
    if (data?.server_fingerprint && data.server_fingerprint.length > 0) {
      try {
        const urlStr = data.server_fingerprint[0].target_url;
        const url = new URL(urlStr);
        return url.hostname;
      } catch {
        return data.server_fingerprint[0].target_url;
      }
    }
    if (flatFindings.length > 0 && flatFindings[0].url) {
      try {
        const urlStr = flatFindings[0].url;
        const url = new URL(urlStr);
        return url.hostname;
      } catch {
        return flatFindings[0].url;
      }
    }
    return "";
  };

  const domainName = getTargetDomain();

  const handleSeverityCardClick = (severity: string) => {
    setVulnerabilitySeverityFilter(severity);
    setActiveTab("findings");
  };

  if (!clientId || level?.name === "global" || clientId !== initialClientIdRef.current) {
    return null;
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 p-4 pb-20 min-h-screen text-gray-900 dark:text-gray-200">
      <div className="w-full mx-auto space-y-4">
        {/* Navigation & Header */}
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="bg-orange-600 p-1.5 rounded shadow-lg">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-gray-900 dark:text-gray-100 leading-none">
                External Scan Results
              </h1>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] font-mono font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 px-1.5 py-0.2 rounded">
                  Job: {jobId}
                </span>
                {domainName && (
                  <span className="text-[10px] font-bold text-orange-600 dark:text-orange-500 uppercase tracking-wider bg-orange-50 dark:bg-orange-500/10 px-1.5 py-0.2 rounded border border-orange-200 dark:border-orange-500/15">
                    {domainName}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-end gap-3 shrink-0">
            {assetId && (
              <div className="w-64">
                <SingleSelectApiDropdown
                  label="Scans"
                  placeholder="Select Scan Job"
                  apiEndpoint={`/external_scan/jobs/${assetId}`}
                  value={currentJob}
                  onChange={(selected: any) => {
                    if (selected && selected.job_id && assetId) {
                      navigate(`/external-scan/results/${assetId}?jobid=${selected.job_id}`);
                    }
                  }}
                  displayProperty="created"
                  secondaryDisplayProperty="status"
                  idProperty="job_id"
                  searchFields={["status", "job_id"]}
                  searchInputPlaceholder="Search by status or Job ID..."
                  getDisplayText={(item) => {
                    if (!item.created) return `Job: ${item.job_id?.slice(0, 8) || ""}`;
                    try {
                      return new Date(`${item.created}Z`).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      });
                    } catch {
                      return item.created;
                    }
                  }}
                  height="h-10"
                  buttonClassName="!py-2 !text-xs !bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 hover:!border-orange-500/50 text-gray-900 dark:text-gray-100"
                  showSecondaryDisplayProperty={true}
                  clearDisabled={true}
                />
              </div>
            )}

            <button
              onClick={() => navigate("/external-scan")}
              className="flex items-center justify-center gap-1 bg-orange-600 hover:bg-orange-500 text-white rounded-lg px-3 py-1.5 text-[13px] font-bold transition-all shrink-0 h-10 cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4 text-white" />
              Back
            </button>
          </div>
        </div>

        {/* Tabbed Panel */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl flex flex-col">
          {/* Tab buttons */}
          <div className="px-6 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700/50 flex gap-2 overflow-x-auto">
            {[
              { id: "overview", label: "Overview", icon: Info },
              { id: "subdomains", label: "Subdomains", icon: Globe, count: data?.subdomains?.length },
              { id: "findings", label: "Vulnerabilities", icon: ShieldAlert, count: flatFindings.length },
              { id: "server", label: "Server & Tech", icon: Server }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all duration-150 whitespace-nowrap ${isActive
                    ? "border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50/50 dark:bg-orange-500/5"
                    : "border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100/60 dark:hover:bg-gray-800/30"
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${isActive ? "bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300" : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"}`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Active Tab View */}
          <div className="bg-white dark:bg-gray-900/10 min-h-[50vh]">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-3" />
                <p className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest italic animate-pulse">
                  Analyzing Scan Results...
                </p>
              </div>
            ) : !data ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-600 dark:text-gray-300">
                <ShieldAlert className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  No scan result details loaded.
                </p>
              </div>
            ) : (
              <div className="p-6">
                {activeTab === "overview" && (
                  <OverviewTab
                    data={data}
                    flatFindings={flatFindings}
                    severityStats={severityStats}
                    onSeverityClick={handleSeverityCardClick}
                  />
                )}

                {activeTab === "subdomains" && (
                  <SubdomainsTab subdomains={data.subdomains} />
                )}

                {activeTab === "findings" && (
                  <VulnerabilitiesTab
                    findings={flatFindings}
                    initialSeverity={vulnerabilitySeverityFilter}
                  />
                )}

                {activeTab === "server" && (
                  <ServerTechTab fingerprints={data.server_fingerprint} ports={data.ports} technologies={data.technologies} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
